/**
 * Strategy badge styling shared between the (lazily-loaded) AnalyticsDashboard
 * and the synchronously-rendered deck strategy badges in CollectionView.
 *
 * Kept in its own module so CollectionView can import it without pulling the
 * code-split AnalyticsDashboard chunk into the main bundle.
 */
export function getStrategyBadgeStyle(strategy: string): { bg: string; text: string; border: string } {
  if (strategy === 'General / Goodstuff') {
    return { bg: 'bg-gray-900/50', text: 'text-gray-400', border: 'border-gray-800' };
  }

  // Predefined specific strategies keep their curated styles
  const staticStyles: Record<string, { bg: string; text: string; border: string }> = {
    'Aristocrats': { bg: 'bg-red-950/40', text: 'text-red-400', border: 'border-red-500/20' },
    'Artifacts': { bg: 'bg-cyan-950/40', text: 'text-cyan-400', border: 'border-cyan-500/20' },
    'Spellslinger': { bg: 'bg-indigo-950/40', text: 'text-indigo-400', border: 'border-indigo-500/20' },
    'Enchantress': { bg: 'bg-emerald-950/40', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    'Graveyard': { bg: 'bg-purple-950/40', text: 'text-purple-400', border: 'border-purple-500/20' },
    'Tokens': { bg: 'bg-amber-950/40', text: 'text-amber-400', border: 'border-amber-500/20' },
    'Voltron': { bg: 'bg-orange-950/40', text: 'text-orange-400', border: 'border-orange-500/20' },
    'Stax': { bg: 'bg-rose-950/40', text: 'text-rose-400', border: 'border-rose-500/20' },
    'Planeswalkers': { bg: 'bg-pink-950/40', text: 'text-pink-400', border: 'border-pink-500/20' },
    'Landfall': { bg: 'bg-lime-950/40', text: 'text-lime-400', border: 'border-lime-500/20' },
    '+1/+1 Counters': { bg: 'bg-teal-950/40', text: 'text-teal-400', border: 'border-teal-500/20' },
    'Blink': { bg: 'bg-sky-950/40', text: 'text-sky-400', border: 'border-sky-500/20' },
    'Lifegain': { bg: 'bg-green-950/40', text: 'text-green-400', border: 'border-green-500/20' },
    'Group Hug': { bg: 'bg-blue-950/40', text: 'text-blue-400', border: 'border-blue-500/20' }
  };

  if (staticStyles[strategy]) {
    return staticStyles[strategy]!;
  }

  // Dynamic palette for EDHREC specific themes/tags
  const dynamicPalettes = [
    { bg: 'bg-red-950/40', text: 'text-red-400', border: 'border-red-500/20' },
    { bg: 'bg-cyan-950/40', text: 'text-cyan-400', border: 'border-cyan-500/20' },
    { bg: 'bg-indigo-950/40', text: 'text-indigo-400', border: 'border-indigo-500/20' },
    { bg: 'bg-emerald-950/40', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    { bg: 'bg-purple-950/40', text: 'text-purple-400', border: 'border-purple-500/20' },
    { bg: 'bg-amber-950/40', text: 'text-amber-400', border: 'border-amber-500/20' },
    { bg: 'bg-orange-950/40', text: 'text-orange-400', border: 'border-orange-500/20' },
    { bg: 'bg-rose-950/40', text: 'text-rose-400', border: 'border-rose-500/20' },
    { bg: 'bg-pink-950/40', text: 'text-pink-400', border: 'border-pink-500/20' },
    { bg: 'bg-lime-950/40', text: 'text-lime-400', border: 'border-lime-500/20' },
    { bg: 'bg-teal-950/40', text: 'text-teal-400', border: 'border-teal-500/20' },
    { bg: 'bg-sky-950/40', text: 'text-sky-400', border: 'border-sky-500/20' },
    { bg: 'bg-green-950/40', text: 'text-green-400', border: 'border-green-500/20' },
    { bg: 'bg-blue-950/40', text: 'text-blue-400', border: 'border-blue-500/20' }
  ];

  let hash = 0;
  for (let i = 0; i < strategy.length; i++) {
    hash = strategy.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % dynamicPalettes.length;
  return dynamicPalettes[index]!;
}
